"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { getImageUrl } from "../../utils/helper"
import AnalyticsService from "services/analytics.service"
import { EVENT_NAME } from "utils/constants"
import { MdOutlineInfo, MdSearch } from "react-icons/md"

const SHOP_TIMINGS = {
  // 'fe01e5b0-9439-417f-ab59-27845d0a76df': { open: '06:00', close: '18:00' },
}

const isShopOpen = (merchantId) => {
  if (!SHOP_TIMINGS[merchantId]) return true

  const now = new Date()
  const currentHours = now.getHours()
  const currentMinutes = now.getMinutes()
  const currentTime = currentHours * 60 + currentMinutes

  const [openHour, openMinute] = SHOP_TIMINGS[merchantId].open.split(":").map(Number)
  const [closeHour, closeMinute] = SHOP_TIMINGS[merchantId].close.split(":").map(Number)

  const openTime = openHour * 60 + openMinute
  const closeTime = closeHour * 60 + closeMinute

  return currentTime >= openTime && currentTime <= closeTime
}

const ProductCard = ({
  product,
  commonProductList = [],
  onProductChange,
  cart,
  clearFromCart,
  setCart,
  isRepairPage = false,
  onProductSelect, // New prop for search placeholder animation
}) => {
  const [showPriceTooltip, setShowPriceTooltip] = useState(false)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [showCartAnimation, setShowCartAnimation] = useState(false)

  // Determine if the shop is open
  const isOpen = isShopOpen(product.merchantId)

  const handleAddToCart = (product, selectedShop) => {
    if (!isOpen) return // Prevent adding to cart if shop closed

    setCart((prevCart) => ({
      ...prevCart,
      [product.id]: {
        ...product,
        quantity: product.minQuantity || 1,
      },
    }))
    
    // Trigger cart animation
    setShowCartAnimation(true)
    setTimeout(() => setShowCartAnimation(false), 5000)

    AnalyticsService.trackEvent(EVENT_NAME.CART_PRODUCT_ADDED, {
      productId: product.id,
      productName: product.name,
      minQuantity: product.minQuantity || 1,
      merchantId: product.merchantId,
      merchantName: product.merchant?.displayName,
      categoryId: product.categoryId,
    })
  }

  const handleIncreaseQuantity = (product) => {
    setCart((prevCart) => ({
      ...prevCart,
      [product.id]: {
        ...prevCart[product.id],
        quantity: prevCart[product.id].quantity + 1,
      },
    }))
    
    // Trigger animation
    setShowCartAnimation(true)
    setTimeout(() => setShowCartAnimation(false), 500)

    AnalyticsService.trackEvent(EVENT_NAME.CART_PRODUCT_QUANTITY_INCREASED, {
      productId: product.id,
      productName: product.name,
      merchantName: product.merchant?.displayName,
    })
  }

  const handleDecreaseQuantity = (product) => {
    setCart((prevCart) => {
      const updatedCart = { ...prevCart }
      const existingProduct = prevCart[product.id]

      // Prevent quantity from going below minQuantity
      if (existingProduct.quantity > (product.minQuantity || 1)) {
        updatedCart[product.id].quantity -= 1
      } else {
        delete updatedCart[product.id]
      }
      return updatedCart
    })

    AnalyticsService.trackEvent(EVENT_NAME.CART_PRODUCT_QUANTITY_DECREASED, {
      productId: product.id,
      productName: product.name,
      merchantName: product.merchant?.displayName,
      merchantId: product.merchantId,
    })
  }

  const renderStars = (rating) => {
    return Math.round(rating * 10) / 10
  }

  return (
    <>
      <motion.div
        className={`product_details_page_product_card ${!isOpen ? "product_details_page_shop_closed" : ""}`}
        key={product.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <div className="product_details_page_product_image_container">
          <img
            src={getImageUrl(product.imageUrl) || "/placeholder.svg"}
            alt={product.name}
            className="product_details_page_product_image"
            onClick={() => setIsPopupOpen(true)}
          />
          <div className="product_details_page_veg_indicator">
            <div className="product_details_page_veg_dot"></div>
          </div>
        </div>

        <div className="product_details_page_product_info">
          <h3 
            className="product_details_page_product_name"
            // onClick={() => onProductSelect(product.name)}
          >
            {product.name}
          </h3>

          {product.rating && (
            <div className="product_details_page_rating_container">
              <div className="product_details_page_rating_badge">
                <span>★</span>
                <span>{renderStars(product.rating)}</span>
              </div>
              <span className="product_details_page_rating_count">(571)</span>
              <span className="product_details_page_delivery_time">9 mins</span>
            </div>
          )}

          {/* Shop closed message */}
          {!isOpen && (
            <div className="product_details_page_shop_closed_message">
              Shop is closed for today (Opens at {SHOP_TIMINGS[product.merchantId]?.open || "--:--"})
            </div>
          )}

          {!isRepairPage && product.merchant && (
            <div className="product_details_page_shop_info">
              Shop: <strong>{product.merchant?.displayName}</strong>
            </div>
          )}

          {product.isPriceBasedOnLocation ? (
            <p className="product_details_page_price_container">
              <span className="product_details_page_current_price">Fees: लोकेशन के हिसाब से</span>
            </p>
          ) : (
            <div className="product_details_page_price_container">
              <span className="product_details_page_current_price">
                ₹{cart[product.id]?.FinalPrice || product.sellingPrice}
              </span>
              {product.sellingPrice !== product.listingPrice && (
                <>
                  <span className="product_details_page_original_price">
                    ₹{cart[product.id]?.price || product.listingPrice}
                  </span>
                  <span className="product_details_page_discount_badge">
                    ₹
                    {(cart[product.id]?.price || product.listingPrice) -
                      (cart[product.id]?.FinalPrice || product.sellingPrice)}{" "}
                    Off
                  </span>
                </>
              )}
              <span
                className="product_details_page_price_info_icon"
                onMouseEnter={() => setShowPriceTooltip(true)}
                onMouseLeave={() => setShowPriceTooltip(false)}
              >
                <MdOutlineInfo size={12} style={{ color: "#666" }} />
                {showPriceTooltip && (
                  <div className="product_details_page_price_tooltip">• Platform fee included  <br/> • MRP may vary</div>
                )}
              </span>
            </div>
          )}

          {product.minQuantity > 1 && (
            <p className="product_details_page_min_quantity_text">Min Order Quantity: {product.minQuantity}</p>
          )}

          {commonProductList.length > 0 && (
            <div className="product_details_page_shop_selection">
              <div className="product_details_page_change_shop_text">change shop</div>
              <select
                value={product.id}
                onChange={(e) => onProductChange(e.target.value)}
                className="product_details_page_shop_dropdown"
              >
                {commonProductList.map((commonProduct) => (
                  <option key={commonProduct.id} value={commonProduct.id}>
                    {commonProduct.merchant.displayName} - ₹{commonProduct.sellingPrice}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="product_details_page_action_container">
            {cart[product.id] ? (
              <div className="product_details_page_quantity_controls">
                {!isRepairPage && (
                  <>
                    <motion.button
                      className="product_details_page_quantity_button"
                      onClick={() => handleDecreaseQuantity(product)}
                      whileTap={{ scale: 0.9 }}
                    >
                      -
                    </motion.button>
                    <motion.span 
                      className="product_details_page_quantity_display"
                      key={`quantity-${cart[product.id]?.quantity}-${product.id}`}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {cart[product.id]?.quantity}
                    </motion.span>
                    <motion.button
                      className="product_details_page_quantity_button"
                      onClick={() => handleIncreaseQuantity(product)}
                      whileTap={{ scale: 0.9 }}
                    >
                      +
                    </motion.button>
                  </>
                )}
                <motion.button 
                  className="product_details_page_remove_button" 
                  onClick={() => clearFromCart(product.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Remove
                </motion.button>
              </div>
            ) : (
              <motion.button
                className="product_details_page_add_button"
                onClick={() => handleAddToCart(product, product.merchant)}
                disabled={!isOpen}
                whileHover={!isOpen ? {} : { scale: 1.05 }}
                whileTap={!isOpen ? {} : { scale: 0.95 }}
              >
                {!isOpen ? "Shop Closed" : isRepairPage ? "Book Service" : "ADD"}
              </motion.button>
            )}
          </div>
          
          {/* Cart animation */}
          <AnimatePresence>
            {showCartAnimation && (
              <motion.div
                className="product_details_page_cart_animation"
                initial={{ opacity: 1, scale: 1 }}
                animate={{ opacity: 0, scale: 1.5, y: -50 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <div className="product_details_page_cart_animation_icon">🛒</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  )
}

export default ProductCard